import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Review } from "../../../api/content/reviewDecoders";
import { appPaths, entryNumberOfFragment } from "../../../shared/routing/appPaths";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import CopyReference from "../../../ui/content/CopyReference";
import { readableChangeSetTitle } from "../../changeSets/changeSetTitle";
import { useMemberNames } from "../../threads/data/useMemberNames";
import ReviewStateBadge from "../list/ReviewStateBadge";

/**
 * Title, state, who proposed it when, and the Thread entries it was saved from. `compact` in
 * a Thread's current step, whose header already names the step.
 */
export default function ReviewHeader({ review, compact = false }: { review: Review; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const people = useMemberNames(review.changeSet.projectId);
  const changeSet = review.changeSet;
  const time = formatTimeAgo(changeSet.openedAt, i18n.resolvedLanguage);
  return (
    <header className="space-y-1">
      <div className="flex flex-wrap items-start gap-2">
        {compact ? (
          <h4 className="app-title flex-1 text-sm font-semibold">{readableChangeSetTitle(changeSet.title)}</h4>
        ) : (
          <h2 className="app-title flex-1 text-xl font-bold">{readableChangeSetTitle(changeSet.title)}</h2>
        )}
        <ReviewStateBadge state={changeSet.state} />
      </div>
      <p className="app-text-muted flex flex-wrap items-center gap-x-1 text-sm">
        {changeSet.openedBy === people.currentUserId
          ? t("reviews.page.proposedByYou", { time })
          : t("reviews.page.proposed", { name: review.proposerName ?? people.nameOf(changeSet.openedBy), time })}
        <CopyReference value={review.head} label={t("reviews.page.copyHead")} />
      </p>
      {changeSet.originThreadEntries.length > 0 ? (
        <p className="app-text-muted text-sm">
          {t("reviews.page.savedFrom")}{" "}
          {changeSet.originThreadEntries.map((reference) => {
            const [threadId = "", fragment = ""] = reference.split("#");
            return (
              <Link key={reference} to={appPaths.thread(threadId, entryNumberOfFragment(fragment))} className="app-link mr-2 underline">
                {t("reviews.page.threadEntry", { number: fragment })}
              </Link>
            );
          })}
        </p>
      ) : null}
    </header>
  );
}
